import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { tap, switchMap, catchError } from 'rxjs/operators';
import * as i3 from '@angular/router';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { EMPTY, throwError } from 'rxjs';

const AUTH_CONFIG = new InjectionToken('AUTH_CONFIG');

class HttpService {
    constructor(http, authConfig) {
        this.http = http;
        this.authConfig = authConfig;
    }
    createAccount(registrationDetails) {
        return this.http.post(this.authConfig.registrationPath, registrationDetails);
    }
    login(loginDetails) {
        return this.http.post(this.authConfig.loginPath, loginDetails);
    }
    refreshAccessToken(refreshToken) {
        return this.http.post(this.authConfig.refreshTokenPath, `grant_type=refresh_token&refresh_token=${refreshToken}`, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
    }
    setPassword(setPassword) {
        return this.http.post(this.authConfig.setPasswordPath, setPassword);
    }
    requestResetPassword(username) {
        return this.http.post(this.authConfig.requestResetPath, { username });
    }
}
HttpService.ɵfac = function HttpService_Factory(t) { return new (t || HttpService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(AUTH_CONFIG)); };
HttpService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: HttpService, factory: HttpService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HttpService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                type: Inject,
                args: [AUTH_CONFIG]
            }] }]; }, null); })();

class StorageService {
    constructor() { }
    store(key, value) {
        if (typeof value === 'string') {
            localStorage.setItem(key, value);
        }
        else {
            try {
                const valueAsString = JSON.stringify(value);
                localStorage.setItem(key, valueAsString);
            }
            catch (e) {
                throw new Error('Only strings and JSON can be stored');
            }
        }
    }
    retrieve(key) {
        const value = localStorage.getItem(key);
        try {
            return JSON.parse(value);
        }
        catch {
            return value;
        }
    }
    remove(key) {
        localStorage.removeItem(key);
    }
    storeAuthDetails(authDetails) {
        this.store('authDetails', authDetails);
    }
    clearAuthDetails() {
        this.remove('authDetails');
    }
    retrieveAuthDetails() {
        return this.retrieve('authDetails');
    }
}
StorageService.ɵfac = function StorageService_Factory(t) { return new (t || StorageService)(); };
StorageService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: StorageService, factory: StorageService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(StorageService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

class TokenService {
    constructor(restService, storageService, authConfig) {
        this.restService = restService;
        this.storageService = storageService;
        this.authConfig = authConfig;
    }
    refreshAccessToken() {
        const refreshToken = this.getRefreshToken();
        return this.restService.refreshAccessToken(refreshToken);
    }
    getAuth() {
        return this.storageService.retrieveAuthDetails();
    }
    getAccessToken() {
        if (this.getAuth()) {
            return this.getAuth().access_token;
        }
    }
    getRefreshToken() {
        if (this.getAuth()) {
            return this.getAuth().refresh_token;
        }
    }
    /*
    Need to test this out in the app now
     */
    // This method should be customised based on the SpringSecurityRest plugin configuration.
    isUnauthenticatedUrl(url) {
        return url.includes(this.authConfig.refreshTokenPath) ||
            url.includes(this.authConfig.loginPath) ||
            url.includes(this.authConfig.registrationPath) ||
            this.urlIncludesPath(url);
    }
    urlIncludesPath(url) {
        if (!this.authConfig.skipAuthUrlPathPartials?.length) {
            return false;
        }
        return this.authConfig.skipAuthUrlPathPartials.some(skipUrl => url.includes(skipUrl));
    }
    setAuth(newCredentials) {
        this.storageService.storeAuthDetails(newCredentials);
    }
}
TokenService.ɵfac = function TokenService_Factory(t) { return new (t || TokenService)(i0.ɵɵinject(HttpService), i0.ɵɵinject(StorageService), i0.ɵɵinject(AUTH_CONFIG)); };
TokenService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: TokenService, factory: TokenService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TokenService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: HttpService }, { type: StorageService }, { type: undefined, decorators: [{
                type: Inject,
                args: [AUTH_CONFIG]
            }] }]; }, null); })();

class CredentialsService {
    constructor(restService, storageService, router, authConfig) {
        this.restService = restService;
        this.storageService = storageService;
        this.router = router;
        this.authConfig = authConfig;
    }
    login(loginDetails) {
        return this.restService.login(loginDetails)
            .pipe(tap(authDetails => this.storageService.storeAuthDetails(authDetails)));
    }
    logout() {
        this.storageService.clearAuthDetails();
        this.router.navigate([`${this.authConfig.loginPageRoute}`]);
    }
    requestResetPassword(username) {
        return this.restService.requestResetPassword(username);
    }
    createAccount(registrationDetails) {
        return this.restService.createAccount(registrationDetails);
        return this.restService.createAccount(registrationDetails)
            .pipe(tap(authDetails => {
            if (authDetails.hasOwnProperty('access_token')) {
                this.storageService.storeAuthDetails(authDetails);
            }
        }));
    }
    setPassword(setPassword) {
        return this.restService.setPassword(setPassword);
    }
    setLoginPageRoute(newRoute) {
        this.authConfig.loginPageRoute = newRoute;
    }
}
CredentialsService.ɵfac = function CredentialsService_Factory(t) { return new (t || CredentialsService)(i0.ɵɵinject(HttpService), i0.ɵɵinject(StorageService), i0.ɵɵinject(Router), i0.ɵɵinject(AUTH_CONFIG)); };
CredentialsService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: CredentialsService, factory: CredentialsService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CredentialsService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: HttpService }, { type: StorageService }, { type: i3.Router, decorators: [{
                type: Inject,
                args: [Router]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [AUTH_CONFIG]
            }] }]; }, null); })();

class AngularAuthService {
    constructor(tokenService, credentialsService) {
        this.tokenService = tokenService;
        this.credentialsService = credentialsService;
    }
    /**
     * Create an account
     * @param {string} username
     * @param extraInfo this is an object of extra info, eg: {firstName: 'Michael', lastName: 'Simmonds'}
     */
    createAccount(username, extraInfo) {
        return this.credentialsService.createAccount({ username, extraInfo });
    }
    /**
     * Login to account with username/password combination. ~Username is normally an username address.~
     * @param {string} username
     * @param {string} password
     */
    login(username, password) {
        return this.credentialsService.login({ username, password });
    }
    /**
     * Logout
     */
    logout() {
        this.credentialsService.logout();
    }
    /**
     * Request a password reset link to username address
     * @param {string} username
     */
    requestResetPassword(username) {
        return this.credentialsService.requestResetPassword(username);
    }
    setPassword(setPassword) {
        return this.credentialsService.setPassword(setPassword);
    }
    /**
     * Sets a new the login page route. The app will redirect to this page in case the user logs out.
     * @param {string} newRoute
     */
    setLoginPageRoute(newRoute) {
        console.log('setLoginPageRoute');
        return this.credentialsService.setLoginPageRoute(newRoute);
    }
}
AngularAuthService.ɵfac = function AngularAuthService_Factory(t) { return new (t || AngularAuthService)(i0.ɵɵinject(TokenService), i0.ɵɵinject(CredentialsService)); };
AngularAuthService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: AngularAuthService, factory: AngularAuthService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AngularAuthService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: TokenService }, { type: CredentialsService }]; }, null); })();

class AngularAuthModule {
    static forRoot(angularAuthConfig) {
        return {
            ngModule: AngularAuthModule,
            providers: [
                {
                    provide: AUTH_CONFIG,
                    useValue: angularAuthConfig
                }
            ]
        };
    }
}
AngularAuthModule.ɵfac = function AngularAuthModule_Factory(t) { return new (t || AngularAuthModule)(); };
AngularAuthModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: AngularAuthModule });
AngularAuthModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [CommonModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AngularAuthModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                imports: [
                    CommonModule
                ]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(AngularAuthModule, { imports: [CommonModule] }); })();

class TokenInterceptorService {
    constructor(authService, credentialsService) {
        this.authService = authService;
        this.credentialsService = credentialsService;
    }
    addAuthHeaders(req, token) {
        if (!token) {
            return req;
        }
        else {
            return req.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`,
                }
            });
        }
    }
    handle401Error(req, next, error) {
        return this.authService.refreshAccessToken()
            .pipe(switchMap((newCredentials) => {
            const newAccessToken = newCredentials.access_token;
            if (newAccessToken) {
                this.storeAuthCredentials(newCredentials);
                return next.handle(this.addAuthHeaders(req, newAccessToken));
            }
        }));
    }
    intercept(req, next) {
        if (this.isUnauthenticatedUrl(req.url)) {
            return next.handle(req);
        }
        else {
            return next.handle(this.addAuthHeaders(req, this.getAccessToken()))
                .pipe(catchError(accessError => {
                if (this.accessTokenIsInvalid(accessError)) {
                    if (req.url.includes('/login')) {
                        throw accessError;
                    }
                    return this.handle401Error(req, next, accessError)
                        .pipe(catchError(() => {
                        this.forceLogout();
                        return EMPTY;
                    }));
                }
                else {
                    return throwError(accessError);
                }
            }));
        }
    }
    isUnauthenticatedUrl(url) {
        return this.authService.isUnauthenticatedUrl(url);
    }
    accessTokenIsInvalid(error) {
        return error instanceof HttpErrorResponse && error.status === 401;
    }
    getAccessToken() {
        return this.authService.getAccessToken() || undefined;
    }
    forceLogout() {
        this.credentialsService.logout();
    }
    storeAuthCredentials(newCredentials) {
        this.authService.setAuth(newCredentials);
    }
}
TokenInterceptorService.ɵfac = function TokenInterceptorService_Factory(t) { return new (t || TokenInterceptorService)(i0.ɵɵinject(TokenService), i0.ɵɵinject(CredentialsService)); };
TokenInterceptorService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: TokenInterceptorService, factory: TokenInterceptorService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TokenInterceptorService, [{
        type: Injectable
    }], function () { return [{ type: TokenService }, { type: CredentialsService }]; }, null); })();

/*
 * Public API Surface of angular-auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_CONFIG, AngularAuthModule, AngularAuthService, TokenInterceptorService };
